import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Zap, Briefcase, MapPin, Flame } from "lucide-react";
import { toast } from "sonner";
import { GIG_TAGS, TAG_LABEL, type GigTag } from "@/lib/gigTags";
import { WORKER_SKILLS, SKILL_LABEL, type WorkerSkill } from "@/lib/workerSkills";
import { TierBadge, type Tier } from "@/components/TierBadge";
import { formatMoney } from "@/lib/money";
import { NotificationsBell } from "@/components/NotificationsBell";

type Urgency = "normal" | "urgent" | "now";
type PayoutType = "fixed" | "hourly";

const NewGig = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState<GigTag>("delivery");
  const [budget, setBudget] = useState("");
  const [location, setLocation] = useState("");
  const [type, setType] = useState<"gig" | "drop">("gig");
  const [hours, setHours] = useState("24");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tier, setTier] = useState<Tier>("basic");
  const [maxBudgetCents, setMaxBudgetCents] = useState<number | null>(null);

  // Phase 2 additions
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [workersNeeded, setWorkersNeeded] = useState("1");
  const [urgency, setUrgency] = useState<Urgency>("normal");
  const [payoutType, setPayoutType] = useState<PayoutType>("fixed");
  const [contactMethod, setContactMethod] = useState("");
  const [skills, setSkills] = useState<WorkerSkill[]>([]);
  const [instantAccept, setInstantAccept] = useState(false);
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(null);
  const [pinningGps, setPinningGps] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: p } = await supabase.from("profiles").select("tier").eq("user_id", user.id).maybeSingle();
      const t = ((p as any)?.tier ?? "basic") as Tier;
      setTier(t);
      const { data: lim } = await supabase
        .from("tier_limits")
        .select("max_gig_budget_cents")
        .eq("tier", t)
        .maybeSingle();
      if (lim) setMaxBudgetCents((lim as any).max_gig_budget_cents);
    })();
  }, [user]);

  const toggleSkill = (s: WorkerSkill) =>
    setSkills((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const captureGps = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setPinningGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setPinningGps(false);
        toast.success("Location pinned");
      },
      (err) => {
        setPinningGps(false);
        toast.error(err.message || "Could not get location");
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(budget) * 100);
    if (!cents || cents < 100) return toast.error("Budget must be at least $1");
    if (maxBudgetCents !== null && cents > maxBudgetCents) {
      return toast.error(`Your ${tier} tier caps gigs at ${formatMoney(maxBudgetCents)}.`);
    }
    if (!/^\d{4}$/.test(pin)) return toast.error("Enter your 4-digit PIN");
    const wn = Math.max(1, Math.min(50, parseInt(workersNeeded, 10) || 1));
    if (startTime && endTime && new Date(endTime) <= new Date(startTime))
      return toast.error("End time must be after start time");

    setSubmitting(true);
    const { data, error } = await supabase.rpc("create_gig", {
      _title: title,
      _description: description,
      _tag: tag,
      _budget_cents: cents,
      _type: urgency === "now" ? "drop" : type,
      _location: location || null,
      _expires_in_hours:
        type === "drop" || urgency === "now" ? parseInt(hours, 10) || 24 : null,
      _pin: pin,
    });
    if (error) {
      setSubmitting(false);
      return toast.error(error.message);
    }
    const newId = (data as any).id as string;

    // Enrich with Phase 2 fields (poster RLS + guard trigger restrict to safe columns)
    const { error: upErr } = await supabase
      .from("gigs")
      .update({
        start_time: startTime ? new Date(startTime).toISOString() : null,
        end_time: endTime ? new Date(endTime).toISOString() : null,
        workers_needed: wn,
        urgency,
        payout_type: payoutType,
        contact_method: contactMethod || null,
        skill_tags: skills,
        gps_lat: gps?.lat ?? null,
        gps_lng: gps?.lng ?? null,
        instant_accept: instantAccept,
      } as any)
      .eq("id", newId);
    setSubmitting(false);
    if (upErr) {
      toast.warning("Posted, but extra details didn't save: " + upErr.message);
    } else {
      toast.success("Gig posted — funds in escrow");
    }
    navigate(`/gigs/${newId}`);
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} right={<NotificationsBell />} />
      <div className="px-4 pt-2 pb-4 space-y-3">
        <h1 className="font-display font-extrabold text-2xl">Post a gig</h1>
        <Link
          to="/verification"
          className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2 hover:bg-muted/40 transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0">
            <TierBadge tier={tier} />
            <span className="text-xs text-muted-foreground truncate">
              {maxBudgetCents !== null
                ? `Max budget ${formatMoney(maxBudgetCents)}`
                : "Loading limits…"}
            </span>
          </div>
          <span className="text-xs font-semibold text-primary shrink-0">Upgrade →</span>
        </Link>
      </div>

      <form onSubmit={submit} className="px-4 pb-24 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setType("gig")}
            className={`rounded-xl border p-3 text-left ${
              type === "gig" ? "border-primary bg-primary/10" : "border-border bg-card"
            }`}
          >
            <Briefcase className="h-5 w-5 mb-1" />
            <p className="font-display font-bold text-sm">Gig</p>
            <p className="text-[10px] text-muted-foreground">Standard listing</p>
          </button>
          <button
            type="button"
            onClick={() => setType("drop")}
            className={`rounded-xl border p-3 text-left ${
              type === "drop" ? "border-primary bg-primary/10" : "border-border bg-card"
            }`}
          >
            <Zap className="h-5 w-5 mb-1 text-primary" />
            <p className="font-display font-bold text-sm">Job drop</p>
            <p className="text-[10px] text-muted-foreground">Urgent, time-limited</p>
          </button>
        </div>

        <div className="space-y-1">
          <Label>Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={100}
            placeholder="Need help with…"
          />
        </div>

        <div className="space-y-1">
          <Label>Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            maxLength={1000}
            rows={4}
          />
        </div>

        <div className="space-y-1">
          <Label>Hustle tag</Label>
          <div className="flex flex-wrap gap-1.5">
            {GIG_TAGS.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setTag(t)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                  tag === t
                    ? "bg-foreground text-background border-foreground"
                    : "bg-card border-border"
                }`}
              >
                {TAG_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <Label>Skills needed (optional)</Label>
          <div className="flex flex-wrap gap-1.5">
            {WORKER_SKILLS.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => toggleSkill(s)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                  skills.includes(s)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border"
                }`}
              >
                {SKILL_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Budget (USD)</Label>
            <Input
              type="number"
              min="1"
              step="0.01"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label>Payout</Label>
            <div className="flex gap-1">
              {(["fixed", "hourly"] as PayoutType[]).map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => setPayoutType(p)}
                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-semibold border ${
                    payoutType === p
                      ? "bg-foreground text-background border-foreground"
                      : "bg-card border-border"
                  }`}
                >
                  {p === "fixed" ? "Fixed" : "Per hour"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Workers needed</Label>
            <Input
              type="number"
              min="1"
              max="50"
              value={workersNeeded}
              onChange={(e) => setWorkersNeeded(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Urgency</Label>
            <div className="flex gap-1">
              {(["normal", "urgent", "now"] as Urgency[]).map((u) => (
                <button
                  type="button"
                  key={u}
                  onClick={() => setUrgency(u)}
                  className={`flex-1 px-1 py-2 rounded-lg text-[11px] font-bold uppercase border ${
                    urgency === u
                      ? u === "now"
                        ? "bg-destructive text-destructive-foreground border-destructive"
                        : u === "urgent"
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-foreground text-background border-foreground"
                      : "bg-card border-border"
                  }`}
                >
                  {u === "now" && <Flame className="h-3 w-3 inline mr-0.5" />}
                  {u}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Start</Label>
            <Input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>End</Label>
            <Input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        {(type === "drop" || urgency === "now") && (
          <div className="space-y-1">
            <Label>Drop expires in (hrs)</Label>
            <Input
              type="number"
              min="1"
              max="168"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </div>
        )}

        <div className="space-y-1">
          <Label>Location (optional)</Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="City or remote"
            maxLength={80}
          />
          <button
            type="button"
            onClick={captureGps}
            disabled={pinningGps}
            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary disabled:opacity-60"
          >
            <MapPin className="h-3 w-3" />
            {pinningGps
              ? "Pinning…"
              : gps
                ? `Pinned (${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)})`
                : "Pin job site for GPS check-in"}
          </button>
        </div>

        <div className="space-y-1">
          <Label>Contact method (optional)</Label>
          <Input
            value={contactMethod}
            onChange={(e) => setContactMethod(e.target.value)}
            placeholder="WhatsApp, in-app chat, email…"
            maxLength={120}
          />
        </div>

        <div className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
          <div className="min-w-0">
            <p className="font-display font-bold text-sm">Instant accept</p>
            <p className="text-[11px] text-muted-foreground">
              Skip approval — first qualified applicant gets the job.
            </p>
          </div>
          <Switch checked={instantAccept} onCheckedChange={setInstantAccept} />
        </div>

        <div className="space-y-1">
          <Label>PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="••••"
          />
          <p className="text-xs text-muted-foreground">
            Funds will be held in escrow and released when you confirm completion.
          </p>
        </div>

        <Button type="submit" disabled={submitting} className="w-full rounded-full" size="lg">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post & hold funds"}
        </Button>
      </form>
    </MobileShell>
  );
};

export default NewGig;
